<template>
  <div class="feature-preview-container">
    <!-- Blurred preview content (slot for custom content) -->
    <div class="preview-items">
      <slot name="preview">
        <!-- Default table preview if no slot content provided -->
        <div class="preview-table">
          <table class="table">
            <thead>
              <tr>
                <th v-for="(col, idx) in columns" :key="idx">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIdx) in sampleData" :key="rowIdx">
                <td v-for="(cell, cellIdx) in row" :key="cellIdx">
                  <div v-if="cellIdx === 0" class="d-flex align-items-center">
                    <i :class="rowIcon" class="text-muted mr-2"></i>
                    <span>{{ cell }}</span>
                  </div>
                  <span v-else>{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </slot>
    </div>

    <!-- Overlay with CTA -->
    <div class="preview-overlay" :class="{ 'preview-overlay--noted': notedCompact }">
      <div class="overlay-content" :class="{ 'overlay-content--noted': notedCompact }">
        <i :class="icon" class="overlay-icon"></i>
        <strong>{{ title }}</strong>
        <p v-if="shownDescription">{{ shownDescription }}</p>
        <!-- Unlaunched features (all AI ones): visible as an upsell, but nothing to request. Wins over featureKey. -->
        <button v-if="comingSoon" type="button" class="upsell-btn upsell-btn--soon" data-test="coming-soon-btn" disabled>
          <i class="fa fa-clock-o"></i> Coming soon
        </button>
        <!-- Paid-feature upsells pass featureKey → emails Teamio via the backend. Others keep mailto. -->
        <FeatureRequestButton
          v-else-if="featureKey"
          class="upsell-btn"
          :feature-key="featureKey"
          :feature-label="featureLabel || title"
          :source-page="sourcePage"
        />
        <a v-else :href="contactHref" class="upsell-btn">
          <i class="fa fa-envelope"></i> {{ buttonText }}
        </a>
        <FeatureRequestNote v-if="featureKey && !comingSoon" :feature="featureKey" :compact="compact" />
      </div>
    </div>
  </div>
</template>

<script>
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';

export default {
  name: 'FeaturePreview',

  components: { FeatureRequestButton, FeatureRequestNote },

  props: {
    // Feature isn't launched yet (every AI feature, 2026-09-16): show a disabled "Coming soon" button.
    comingSoon: {
      type: Boolean,
      default: false
    },
    // A card with a FIXED height (the dashboard's preview widgets): the add-on note says it in one short
    // sentence so it is never clipped. Everywhere else the card grows and the full note shows.
    compact: {
      type: Boolean,
      default: false
    },
    // Request-to-enable (see myaccount-service/lib/feature-catalog.js)
    featureKey: {
      type: String,
      default: ''
    },
    featureLabel: {
      type: String,
      default: ''
    },
    sourcePage: {
      type: String,
      default: ''
    },
    // CTA content
    title: {
      type: String,
      default: 'Enable This Feature'
    },
    description: {
      type: String,
      default: 'Contact us to enable this feature'
    },
    icon: {
      type: String,
      default: 'fa fa-star'
    },
    buttonText: {
      type: String,
      default: 'Contact us to enable'
    },
    // Email configuration
    contactEmail: {
      type: String,
      default: 'nilay@teamio.app'
    },
    emailSubject: {
      type: String,
      default: 'Feature Request'
    },
    // Default table preview configuration (used if no slot content)
    columns: {
      type: Array,
      default: () => ['Name', 'Date']
    },
    sampleData: {
      type: Array,
      default: () => [
        ['Sample Item 1', '2 days ago'],
        ['Sample Item 2', '1 week ago'],
        ['Sample Item 3', '2 weeks ago']
      ]
    },
    rowIcon: {
      type: String,
      default: 'fa fa-file-text-o'
    }
  },

  computed: {
    // A fixed-height card that carries the add-on note: the dashboard stylesheet drops the big icon and
    // tightens the padding for it so the whole card fits (AdminPageModern.scss .overlay-content--noted).
    notedCompact() {
      return this.compact && !!this.featureKey && !this.comingSoon;
    },
    // The prop's fallback text ("Contact us to enable…") must never sit above a Coming soon button.
    shownDescription() {
      if (this.comingSoon && this.description === this.$options.props.description.default) return '';
      return this.description;
    },
    contactHref() {
      return `mailto:${this.contactEmail}?subject=${encodeURIComponent(this.emailSubject)}`;
    }
  }
};
</script>

<style lang="scss" scoped>
/*
 * SCOPED, BUT IT REACHES SLOT CONTENT — and that is load-bearing.
 *
 * Vue 2 stamps slot content with the HOST component's scope id in addition to the parent's
 * (patch.js setScope: "for slot content they should also get the scopeId from the host
 * instance"). So `.preview-items .list-item` below matches the markup a CONSUMER passes into
 * the `preview` slot, even though that markup lives in the consumer's .vue file.
 *
 * Every consumer that fills the slot with the list shape — Kanban.vue's tag-alert upsell,
 * DashboardGrid.vue's messages/forms widgets — depends on the `.list-item` / `.item-avatar` /
 * `.item-content` / `.item-header` / `.item-title` / `.item-time` / `.item-preview` /
 * `.item-badge` family being defined HERE. Deleting or renaming one of them silently unstyles
 * those previews on pages that have no rules of their own.
 *
 * Keeping them here (rather than in a global sheet) is also what makes the previews safe under
 * route-level code splitting: this CSS travels with this component into whatever chunk imports
 * it. AdminPage2's AdminPageModern.scss has a similar-looking family, but every rule there is
 * nested under `.dashboard-page` and only restyles these previews ON that page — it is not, and
 * must not become, the provider for anyone else.
 *
 * Pinned by cypress/node-tests/feature-preview-slot-styles.test.js.
 */
.feature-preview-container {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background-color: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-height: 200px;
  height: 100%; // Fill the parent container height
  // One grid cell holds both the blurred rows and the overlay; the cell sizes to the taller one.
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);

  .preview-items {
    grid-area: 1 / 1;
    filter: blur(1px);
    opacity: 0.75;
    pointer-events: none;

    .preview-table {
      .table {
        margin-bottom: 0;

        thead th {
          border-top: none;
          font-weight: 600;
          color: #334155;
          font-size: 0.8125rem;
        }

        tbody td {
          font-size: 0.875rem;
          color: #475569;
          vertical-align: middle;
        }
      }
    }

    // List item styles for slot content
    .list-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 18px;
      border-bottom: 1px solid #e8ecf1;

      &:last-child {
        border-bottom: none;
      }

      .item-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: #3b82f6;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.8125rem;
        flex-shrink: 0;

        &.form-avatar {
          background-color: rgba(58, 191, 148, 0.1);
          color: #3abf94;
        }
      }

      .item-content {
        flex: 1;
        min-width: 0;
      }

      .item-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2px;
      }

      .item-title {
        font-weight: 600;
        color: #334155;
        font-size: 0.875rem;
      }

      .item-time {
        font-size: 0.75rem;
        color: #94a3b8;
      }

      .item-preview {
        font-size: 0.8125rem;
        color: #64748b;
        margin: 0;
      }

      .item-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 0.6875rem;
        font-weight: 500;
        margin-top: 4px;

        &.new-patient {
          background-color: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }
      }
    }

    // Stats row preview for widgets like Campaigns
    .campaigns-preview,
    .stats-preview {
      padding: 16px 20px;

      .stats-row {
        display: flex;
        justify-content: space-around;
        padding: 12px 0;

        .stat-item {
          text-align: center;

          .stat-value {
            display: block;
            font-size: 1.5rem;
            font-weight: 700;
            color: #334155;
          }

          .stat-label {
            font-size: 0.75rem;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }
      }
    }
  }

  // In-flow, stacked over .preview-items in the same grid cell (not position:absolute), so the box
  // grows when the CTA card is taller than the blurred rows — at phone width the card used to be
  // clipped top and bottom (2026-09-16). A page that re-absolutes it (AdminPageModern.scss) still works.
  .preview-overlay {
    grid-area: 1 / 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 12px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.3);
  }

  .overlay-content {
    text-align: center;
    padding: 20px 24px;
    border-radius: 12px;
    width: 100%;
    max-width: 280px;
    background-color: white;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border: 1px solid #e2e8f0;

    .overlay-icon {
      font-size: 1.5rem;
      margin-bottom: 8px;
      display: block;
      color: #3b82f6;
    }

    strong {
      display: block;
      font-size: 0.9375rem;
      font-weight: 600;
      margin-bottom: 4px;
      color: #1e293b;
    }

    p {
      font-size: 0.8125rem;
      margin: 0 0 12px 0;
      color: #64748b;
    }

    .upsell-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 0.8125rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      background-color: #3b82f6;
      color: white;

      &:hover {
        background-color: #2563eb;
        text-decoration: none;
        color: white;
      }

      &.upsell-btn--soon,
      &.upsell-btn--soon:hover {
        border: none;
        background-color: #e2e8f0;
        color: #475569;
        cursor: default;
      }
    }
  }
}
</style>
